import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import i18n from '../../i18n/i18n';
import { useLocale } from '@util/providers/Locale';
import { useStateWithLocalStorage } from '@util/hooks/useStateWithLocalStorage';
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from '@ui/components';

const DEFAULT_DISPLAY_LANGUAGE = 'English (US)';
const DEFAULT_SORT_LOCALE = 'en-US';

let languagesCache = null;
let languagesPromise = null;

function fetchLanguages() {
  if (languagesCache) return Promise.resolve(languagesCache);
  if (languagesPromise) return languagesPromise;
  const url = process.env.INTERNAL_SERVICE_URL + 'admin/locales';
  languagesPromise = axios(url, { method: 'GET' })
    .then((response) => {
      if (response.status === 200) return response;
      throw Error(response.statusText);
    })
    .then((response) => {
      languagesCache = Array.isArray(response.data) ? response.data : [];
      return languagesCache;
    })
    .finally(() => {
      languagesPromise = null;
    });
  return languagesPromise;
}

export default function LanguageSelect() {
  const { selectedLanguage, setSelectedLanguage } = useLocale();
  const [, setHasManualLanguageSelection] = useStateWithLocalStorage(
    'hasManualLanguageSelection',
    false,
    false
  );
  const [dropdownLanguages, setDropdownLanguages] = useState(
    languagesCache || []
  );

  useEffect(() => {
    if (languagesCache) {
      setDropdownLanguages(languagesCache);
      return;
    }
    let cancelled = false;
    fetchLanguages()
      .then((data) => {
        if (!cancelled) setDropdownLanguages(data);
      })
      .catch((err) => {
        console.error(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedDropdownLanguages = useMemo(() => {
    if (dropdownLanguages.length === 0) return [];

    const apiLocales = dropdownLanguages.map((x) => x.code);
    const current = i18n.resolvedLanguage || i18n.language;

    const fullMatch = apiLocales.find((l) => l === current);
    const primaryMatch = apiLocales.find(
      (l) => l.substring(0, 2) === current.substring(0, 2)
    );

    const sortLocale = fullMatch || primaryMatch || DEFAULT_SORT_LOCALE;
    const collator = new Intl.Collator(sortLocale, { sensitivity: 'base' });

    const normalizeName = (name) => (name || '').trim();

    return dropdownLanguages
      .slice()
      .sort((a, b) =>
        collator.compare(normalizeName(a.name), normalizeName(b.name))
      );
  }, [dropdownLanguages]);

  return (
    <UncontrolledDropdown className="block! w-full">
      {sortedDropdownLanguages.length > 0 && (
        <>
          <DropdownToggle
            caret
            color="outline-opaque-secondary"
            data-testid="LocaleLanguageDropdown"
            className="cursor-pointer select-none rounded border align-middle leading-normal font-normal transition-colors duration-150 ease-in-out focus:outline-none disabled:pointer-events-none disabled:opacity-65 px-5 py-2.5 text-base flex! items-center justify-between w-full text-left"
          >
            <span>
              {selectedLanguage ? selectedLanguage : DEFAULT_DISPLAY_LANGUAGE}
            </span>
          </DropdownToggle>

          <DropdownMenu style={{ overflowY: 'auto', maxHeight: 'min(40rem, 40vh)' }}>
            {sortedDropdownLanguages.map((language) => (
              <DropdownItem
                id={language.code}
                key={language.code}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={
                  language.code.substring(0, 2) === i18n.language && true
                }
                onClick={() => {
                  setSelectedLanguage(language.name);
                  i18n.changeLanguage(language.code.substring(0, 2));
                  setHasManualLanguageSelection(true);
                }}
              >
                {language.name}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </>
      )}
    </UncontrolledDropdown>
  );
}
